import { useQuery } from "@tanstack/react-query";
import { liveCurrent } from "@/lib/weatherHourly";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useWeatherData } from "@/hooks/useWeatherData";
import { pickToday, type N8nWeatherCurrent, type N8nWeatherDay } from "@/lib/weather";

export interface SplashBentoStats {
  /** El día de HOY del parte (por fecha local del sitio, no days[0]) — null mientras carga */
  weatherToday: N8nWeatherDay | null;
  /** El tiempo de AHORA: es lo que se enseña, no la previsión del día */
  weatherCurrent: N8nWeatherCurrent | null;
  /** Number of pending tasks scheduled for today */
  tasksToday: number | null;
  /** Number of active pest alerts — null for workers, who see overdue tasks instead */
  activePests: number | null;
  /** The worker's own tasks left over from previous days — null for everyone else */
  overdueTasks: number | null;
  /** True when the third bento cell should show overdue tasks, not active pests */
  isWorker: boolean;
  /** Whether ANY data is still loading (weather is gated separately) */
  isLoading: boolean;
}

/**
 * Lightweight stats for the welcome splash bento.
 *
 * Read-only — never writes anything, never affects business logic.
 * Mirrors the same data the dashboard / worker portal already display.
 */
export function useSplashBentoStats(): SplashBentoStats {
  let companyId: string | undefined;
  let userId: string | undefined;
  let role: string | null = null;
  try {
    const auth = useAuth();
    companyId = auth.company?.id;
    userId = auth.user?.id;
    role = auth.role ?? null;
  } catch {
    companyId = undefined;
  }

  // Weather — already cached.
  let weatherToday: N8nWeatherDay | null = null;
  let weatherCurrent: N8nWeatherCurrent | null = null;
  try {
    const { data } = useWeatherData();
    // pickToday: un payload cacheado que cruza la medianoche trae AYER en
    // days[0] — el widget ya buscaba por fecha local y este hook no.
    weatherToday = pickToday(data?.days, data?.location?.timezone);
    // El mismo «ahora» que el héroe (tramo de 15 min de este minuto).
    weatherCurrent = liveCurrent(data);
  } catch {
    weatherToday = null;
    weatherCurrent = null;
  }

  // Today bounds as ISO timestamps (scheduled_date is timestamptz).
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  ).toISOString();
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999
  ).toISOString();

  const tasksQuery = useQuery({
    queryKey: ["splash-bento-tasks-today", companyId, userId, role, startOfToday],
    queryFn: async () => {
      // Worker → only their own pending/in-progress tasks for today.
      if (role === "worker" && userId) {
        const { count, error } = await supabase
          .from("planner_tasks")
          .select("id", { count: "exact", head: true })
          .eq("assigned_to", userId)
          .gte("scheduled_date", startOfToday)
          .lte("scheduled_date", endOfToday)
          .in("status", ["pending", "in_progress"]);
        if (error) return 0;
        return count ?? 0;
      }
      // Admin / manager / perito → company-wide non-completed/cancelled today.
      if (!companyId) return 0;
      const { count, error } = await supabase
        .from("planner_tasks")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .gte("scheduled_date", startOfToday)
        .lte("scheduled_date", endOfToday)
        .neq("status", "completed")
        .neq("status", "cancelled");
      if (error) return 0;
      return count ?? 0;
    },
    enabled: role === "worker" ? !!userId : !!companyId,
    staleTime: 30_000,
    retry: false,
  });

  const isWorker = role === "worker";

  // Operarios: la tercera celda cuenta SUS tareas de días anteriores sin cerrar, no las
  // plagas activas —un dato de gestión que no les toca hacer nada al respecto—. Mismos
  // criterios que la sección «Pendientes de días anteriores» de su pantalla de Hoy, para
  // que el número del splash y el de la lista coincidan siempre.
  const overdueQuery = useQuery({
    queryKey: ["splash-bento-overdue-tasks", userId, startOfToday],
    queryFn: async () => {
      if (!userId) return 0;
      const { count, error } = await supabase
        .from("planner_tasks")
        .select("id", { count: "exact", head: true })
        .eq("assigned_to", userId)
        .lt("scheduled_date", startOfToday)
        .in("status", ["pending", "in_progress"]);
      if (error) return 0;
      return count ?? 0;
    },
    enabled: isWorker && !!userId,
    staleTime: 30_000,
    retry: false,
  });

  const pestsQuery = useQuery({
    queryKey: ["splash-bento-active-pests", companyId],
    queryFn: async () => {
      if (!companyId) return 0;
      // "Plaga activa" = a session classified as a specific pest that hasn't been
      // resolved or dismissed (deleted rows are gone).
      const { count, error } = await supabase
        .from("pest_scouting_sessions")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("triage_status", "classified")
        .not("triage_pest_id", "is", null)
        .or("review_status.is.null,review_status.neq.resolved");
      if (error) return 0;
      return count ?? 0;
    },
    enabled: !!companyId && !isWorker,
    staleTime: 30_000,
    retry: false,
  });

  const thirdCellQuery = isWorker ? overdueQuery : pestsQuery;

  return {
    weatherToday,
    weatherCurrent,
    tasksToday: tasksQuery.isSuccess ? tasksQuery.data : null,
    activePests: !isWorker && pestsQuery.isSuccess ? pestsQuery.data : null,
    overdueTasks: isWorker && overdueQuery.isSuccess ? overdueQuery.data : null,
    isWorker,
    isLoading: tasksQuery.isLoading || thirdCellQuery.isLoading,
  };
}
